import { useState, useCallback, useEffect } from 'react'
import {
  Zap,
  Users,
  Monitor,
  AppWindow,
  LogIn,
  LogOut,
  Sliders,
  Activity,
  RefreshCw,
  Download,
} from 'lucide-react'
import {
  getActivityFeed,
  getRealtimeSnapshot,
  type ActivityEvent,
  type RealtimeSnapshot,
} from '../../../lib/api'
import { useAutoRefresh } from '../../../hooks/useAutoRefresh'
import StatCard from '../../../components/analytics/StatCard'
import { arrayToCSV, downloadCSV } from '../../../lib/csv-export'
import { AnalyticsNav } from './AnalyticsOverview'

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

function getEventIcon(eventType: string): { icon: React.ElementType; className: string } {
  if (eventType.startsWith('app_')) {
    return { icon: AppWindow, className: 'text-blue-500' }
  }
  if (eventType === 'user_login') {
    return { icon: LogIn, className: 'text-green-500' }
  }
  if (eventType === 'user_logout') {
    return { icon: LogOut, className: 'text-gray-400' }
  }
  if (eventType.startsWith('settings_')) {
    return { icon: Sliders, className: 'text-amber-500' }
  }
  return { icon: Activity, className: 'text-muted-foreground' }
}

export default function ActivityFeed() {
  const [events, setEvents] = useState<ActivityEvent[]>([])
  const [realtime, setRealtime] = useState<RealtimeSnapshot | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      const [eventsData, realtimeData] = await Promise.all([
        getActivityFeed(50),
        getRealtimeSnapshot(),
      ])
      setEvents(eventsData)
      setRealtime(realtimeData)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load activity feed')
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useAutoRefresh(fetchData, {
    baseInterval: 30000,
    maxInterval: 300000,
  })

  function handleExportCSV() {
    if (events.length === 0) return
    const csv = arrayToCSV(events, [
      { key: 'event_type', label: 'Event Type' },
      { key: 'description', label: 'Description' },
      { key: 'user_email', label: 'User Email' },
      { key: 'resource_type', label: 'Resource Type' },
      { key: 'resource_id', label: 'Resource ID' },
      { key: 'created_at', label: 'Timestamp' },
    ])
    downloadCSV(csv, 'analytics-activity-feed.csv')
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-2">Live Activity</h1>
          <p className="text-muted-foreground">
            Real-time platform events and system snapshot
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleExportCSV}
            disabled={events.length === 0}
            className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg hover:bg-secondary transition-colors text-sm disabled:opacity-50"
            title="Download data as CSV"
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-secondary transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <AnalyticsNav />

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && !realtime ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Right Now snapshot panel */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
              </span>
              <h2 className="text-lg font-semibold">Right Now</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard
                label="Running Apps"
                value={realtime?.running_apps ?? 0}
                icon={Zap}
              />
              <StatCard
                label="Active Workspaces"
                value={realtime?.active_workspaces ?? 0}
                icon={Monitor}
              />
              <StatCard
                label="Connected Users"
                value={realtime?.connected_users ?? 0}
                icon={Users}
              />
            </div>
          </div>

          {/* Activity feed list */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Recent Events</h2>
            {events.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No recent activity
              </div>
            ) : (
              <div className="space-y-2">
                {events.map((event) => {
                  const { icon: EventIcon, className: iconColor } = getEventIcon(event.event_type)
                  return (
                    <div
                      key={event.id}
                      className="p-3 rounded-lg border border-border bg-card"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className={`mt-0.5 ${iconColor}`}>
                            <EventIcon className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="text-sm">{event.description}</p>
                            {event.user_email && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {event.user_name || event.user_email}
                              </p>
                            )}
                          </div>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatRelativeTime(event.created_at)}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
